import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, History, MapPin, MapPinOff, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Field, Loader, PageHeader } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import { LocMap, OpenInMaps } from "@/components/location/LocMap";
import { SafeButton, ShareConfirm } from "@/components/location/ShareConfirm";
import { PlacesSection } from "@/components/location/Places";
import { EtaPanel } from "@/components/location/EtaPanel";

const PINK = "var(--glow-connect)";
const ago = (n) => (n < 1 ? "just now" : n < 60 ? `${n} min ago` : n < 1440 ? `${Math.floor(n / 60)} h ago` : `${Math.floor(n / 1440)} d ago`);

function Picker({ hub, d, kid, onDone }) {
  const [custom, setCustom] = useState(false);
  const [f, setF] = useState({ emoji: "😊", label: "", note: "", back_by: "" });
  const [busy, setBusy] = useState(false);
  const post = async (status) => {
    setBusy(true);
    try {
      await api.post(`/organizations/${hub.id}/checkins`, { status, emoji: f.emoji, label: f.label, note: f.note, back_by: f.back_by || null });
      toast.success("Checked in"); setF({ emoji: "😊", label: "", note: "", back_by: "" }); setCustom(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const size = kid ? "min-h-[64px] text-lg" : "min-h-[48px]";
  return (
    <section className="q-card mb-6 p-4" data-testid="checkin-picker">
      <p className="q-label mb-3">Where are you?</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {d.statuses.map((s) => (
          <button key={s.id} disabled={busy} onClick={() => post(s.id)} className={`flex ${size} min-w-[56px] items-center justify-center gap-2 rounded-2xl border border-q-line bg-q-raised px-3 font-semibold text-q-text transition-transform duration-100 active:scale-95`} data-testid={`status-${s.id}`}>
            <span className="text-2xl">{s.emoji}</span>{s.label}
          </button>
        ))}
        <button onClick={() => setCustom(!custom)} className={`flex ${size} min-w-[56px] items-center justify-center rounded-2xl border border-dashed border-q-line px-3 font-semibold text-q-muted`} data-testid="status-custom">✏️ Custom</button>
      </div>
      {custom && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input className="q-input min-h-[44px] w-16 text-center text-xl" maxLength={8} value={f.emoji} onChange={(e) => setF({ ...f, emoji: e.target.value })} aria-label="Emoji" data-testid="custom-emoji-input" />
          <input className="q-input min-h-[44px] min-w-0 flex-1" maxLength={40} placeholder="At the library" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="custom-label-input" />
          <button className="q-btn q-btn-primary min-h-[44px]" disabled={busy} onClick={() => post("custom")} data-testid="custom-post-btn"><Send className="h-4 w-4" /> Post</button>
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
        <Field label="Note (optional)"><input className="q-input min-h-[44px] w-full" maxLength={100} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Pick me up at the side door" data-testid="checkin-note-input" /></Field>
        <Field label="Back by (optional)"><input type="time" className="q-input min-h-[44px] w-full" value={f.back_by} onChange={(e) => setF({ ...f, back_by: e.target.value })} data-testid="checkin-backby-input" /></Field>
      </div>
    </section>
  );
}

function HistoryDialog({ hub, row, onClose }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/organizations/${hub.id}/checkins/history/${row.key}`).then(({ data }) => setH(data.history)).catch((e) => toast.error(errMsg(e))); }, [hub.id, row.key]);
  return (
    <FormDialog open onClose={onClose} title={`${row.name}: last 7 days`} testid="checkin-history">
      {!h ? <Loader /> : !h.length ? <p className="text-sm text-q-muted">No check-ins in the last 7 days.</p> : (
        <ul className="space-y-1 text-sm" data-testid="checkin-history-list">{h.map((c) => <li key={c.id} className="text-q-text">{c.emoji} {c.text || c.label}{c.note ? ` · ${c.note}` : ""} <span className="text-q-muted">· {new Date(c.at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}</span></li>)}</ul>
      )}
    </FormDialog>
  );
}

function LocToggle({ hub, r, onDone }) {
  const flip = () => api.patch(`/organizations/${hub.id}/checkins/location-access/${r.key.slice(4)}`, { enabled: !r.location_on })
    .then(() => { toast.success(`Location ${r.location_on ? "off" : "on"} for ${r.name}`); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return <button className="q-btn q-btn-ghost min-h-[44px] text-xs" onClick={flip} data-testid={`board-loc-toggle-${r.key}`}>{r.location_on ? <MapPin className="h-4 w-4" /> : <MapPinOff className="h-4 w-4" />} Location {r.location_on ? "on" : "off"}</button>;
}

function Row({ hub, r, onHistory, canLoc, onChanged }) {
  const c = r.latest;
  const ask = () => api.post(`/organizations/${hub.id}/checkins/ask`, { target_key: r.key }).then(() => toast.success(`Asked ${r.name.split(" ")[0]} to check in`)).catch((e) => toast.error(errMsg(e)));
  return (
    <li className={`q-card flex flex-wrap items-center gap-3 p-4 transition-opacity duration-200 ${c?.stale ? "opacity-50 grayscale" : ""}`} data-testid={`board-${r.key}`}>
      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl" style={{ background: `rgba(${PINK},0.14)` }}>{c ? c.emoji : "·"}</span>
      <div className="min-w-0 flex-1 basis-[calc(100%-64px)] sm:basis-0">
        <p className="font-semibold text-q-text">{r.name}</p>
        <p className="text-sm text-q-muted" data-testid={`board-status-${r.key}`}>
          {c ? (c.stale
            ? <>Earlier: {c.label}{c.note ? ` · ${c.note}` : ""} · {new Date(c.at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit", ...(hub.timezone ? { timeZone: hub.timezone } : {}) })}</>
            : <>{c.text || c.label}{c.note ? ` · ${c.note}` : ""} · {ago(c.minutes_ago)}</>) : "No check-in yet"}
        </p>
        {c?.back_by_label && <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: `rgb(${PINK})` }}><Clock className="h-3 w-3" /> Back by {c.back_by_label}</p>}
      </div>
      <div className="flex gap-2">
        <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label="History" onClick={onHistory} data-testid={`board-history-${r.key}`}><History className="h-4 w-4" /></button>
        {canLoc && r.role === "kid" && <LocToggle hub={hub} r={r} onDone={onChanged} />}
        {r.can_ask && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={ask} data-testid={`board-ask-${r.key}`}>Ask to check in</button>}
      </div>
      {c?.location && !c.stale && <div className="w-full space-y-1" data-testid={`board-map-${r.key}`}>
        <LocMap lat={c.location.lat} lng={c.location.lng} height={140} testid={`board-map-view-${r.key}`} />
        <OpenInMaps lat={c.location.lat} lng={c.location.lng} label={r.name} testid={`board-open-maps-${r.key}`} />
      </div>}
    </li>
  );
}

export default function WhosWhere() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [hist, setHist] = useState(null);
  const [params] = useSearchParams();
  const [places, setPlaces] = useState(null);
  const [atPlace, setAtPlace] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/checkins`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id]);
  const loadPlaces = useCallback(() => api.get(`/organizations/${hub.id}/places`).then(({ data }) => setPlaces(data)).catch(() => {}), [hub.id]);
  useEffect(() => { load(); loadPlaces(); }, [load, loadPlaces]);
  useLiveRefresh(load, (m) => m.entity === "checkin", 20000);
  if (!d) return <Loader />;
  const kid = hub.my_role === "kid";
  return (
    <div data-testid="whos-where-page">
      <PageHeader eyebrow="Connect" title="Who's where" rgb={PINK} testid="whos-where-title" />
      <p className="mb-4 inline-flex items-center gap-2 text-sm text-q-muted" data-testid="no-gps-note"><MapPinOff className="h-4 w-4" /> {d.note}</p>
      {(d.asked_by.length > 0 || params.get("ask")) && (
        <p className="mb-3 rounded-2xl p-3 font-semibold text-q-text" style={{ background: `rgba(${PINK},0.14)` }} data-testid="asked-banner">
          {d.asked_by.length ? `${d.asked_by.map((a) => a.name).join(" and ")} asked you to check in. Tap one below.` : "Tap where you are."}
        </p>
      )}
      <div className="mb-4 flex flex-wrap gap-2"><SafeButton hub={hub} locOn={d.my_location_on} onDone={load} /></div>
      {places && <PlacesSection hub={hub} places={places.places} canEdit={places.can_edit} onPlace={setAtPlace} onChanged={loadPlaces} />}
      {places && <EtaPanel hub={hub} places={places.places} locOn={d.my_location_on} onChanged={load} />}
      <Picker hub={hub} d={d} kid={kid} onDone={load} />
      <ul className="space-y-2" data-testid="checkin-board">{d.board.map((r) => <Row key={r.key} hub={hub} r={r} onHistory={() => setHist(r)} canLoc={d.can_manage_location} onChanged={load} />)}</ul>
      {atPlace && <ShareConfirm hub={hub} what={{ status: "place", place_id: atPlace.id, title: `At ${atPlace.name} ${atPlace.emoji}` }} locOn={d.my_location_on} onClose={() => setAtPlace(null)} onDone={load} />}
      {hist && <HistoryDialog hub={hub} row={hist} onClose={() => setHist(null)} />}
    </div>
  );
}
